// current live 


import React, { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { ROUTES } from '../../../utils/constants';
import styles from './Header.module.css';
import { shouldFetchBadge } from '../../../utils/authStorage';

// const API_BASE = import.meta.env?.VITE_API_BASE_URL || 'http://192.168.56.1:8000/api';
const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';



function getToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
}

const Header = ({ 
  logo, 
  searchTerm, 
  setSearchTerm, 
  handleSearchFocus,
  handleScrollToTop,
  currentPage, 
  setCurrentPage, 
  openLoginModal, 
  openPostAdModal 
}) => {
  const { user, isAuthenticated, isAdmin, profileImage, logout } = useAuthContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [inboxBadge, setInboxBadge] = useState(0);
  const [appViewActive, setAppViewActive] = useState(false);

  // ✅ Fetch notification badge count
  const fetchBadgeCount = useCallback(async () => {
    if (!isAuthenticated) { 
      setInboxBadge(0); 
      return; 
    }

    const token = getToken();
    if (!token) return;

    // ✅ SUSPENSION CHECK — always runs regardless of debounce.
    try {
      const profileRes = await fetch(`${API_BASE}/auth/profile/`, {
        headers: { Authorization: `Token ${token}` },
      });

      if (profileRes.status === 401 || profileRes.status === 403) {
        console.warn('🔒 Session rejected (401/403) — forcing logout.');
        logout();
        setCurrentPage(ROUTES.HOME);
        return;
      }

      if (profileRes.ok) {
        const profileData = await profileRes.json();
        if (profileData.is_active === false) {
          console.warn('Account suspended — logging out.');
          logout();
          setCurrentPage(ROUTES.HOME);
          return;
        }
      }
    } catch (err) {
      console.error('Suspension check error:', err);
    }

    if (!shouldFetchBadge()) return;
    
    try {
      const [convoRes, likesRes, followersRes, sysMsgRes] = await Promise.all([
        fetch(`${API_BASE}/messages/conversations/`, { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/my/likes/`,               { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/my/followers/`,           { headers: { Authorization: `Token ${token}` } }),
        fetch(`${API_BASE}/auth/system-messages/`,   { headers: { Authorization: `Token ${token}` } }),
      ]);

      const convoData     = convoRes.ok    ? await convoRes.json()    : { conversations: [] };
      const likesData     = likesRes.ok    ? await likesRes.json()    : { likes: [] };
      const followersData = followersRes.ok ? await followersRes.json() : { followers: [] };
      const sysMsgData    = sysMsgRes.ok   ? await sysMsgRes.json()   : { unread_count: 0 };

      const userEmail = (user?.identifier || user?.email || '').toLowerCase();
      const conversations = convoData.conversations || [];

      const unreadDMs = conversations.filter(c => {
        const isBuyer = (c.buyer?.email || '').toLowerCase() === userEmail;
        return isBuyer ? (c.unread_by_buyer > 0) : (c.unread_by_seller > 0);
      }).length;

      const likes = (likesData.likes || []).length;
      const followers = (followersData.followers || []).length;
      const seenLikes = parseInt(localStorage.getItem('seenLikesCount') || '0', 10);
      const seenFollowers = parseInt(localStorage.getItem('seenFollowersCount') || '0', 10);
      const newLikes = Math.max(0, likes - seenLikes);
      const newFollowers = Math.max(0, followers - seenFollowers);

      const imSellingUnread = sysMsgData.unread_count || 0;

      setInboxBadge(unreadDMs + newLikes + newFollowers + imSellingUnread);
    } catch (err) {
      console.error('Badge fetch error:', err);
    }
  }, [isAuthenticated, user, logout, setCurrentPage]);

  useEffect(() => {
    fetchBadgeCount();
    window.addEventListener('focus', fetchBadgeCount);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchBadgeCount();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', fetchBadgeCount);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchBadgeCount]);

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest(`.${styles.userMenu}`)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Close mobile profile dropdown when clicking outside
  useEffect(() => {
    if (!mobileProfileOpen) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest(`.${styles.mobileProfileInline}`)) {
        setMobileProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileProfileOpen]);

  // ✅ Navigation handlers
  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setMobileProfileOpen(false);
    setCurrentPage(ROUTES.HOME);
  };

  const navigateToHome = () => {
    setCurrentPage(ROUTES.HOME);
    if (handleScrollToTop) handleScrollToTop();
  };

  const navigateToProfile = () => {
    setCurrentPage(ROUTES.PROFILE);
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setMobileProfileOpen(false);
  };

  const handleScrollToContact = () => {
    const footer = document.querySelector('footer');
    if (footer) {
      footer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <header className={styles.mainHeader}>
      <div className={styles.navContainer}>
        {/* ✅ Logo */}
        <img 
          src={logo} 
          alt="I'm Selling Logo" 
          className={styles.siteLogo} 
          onClick={navigateToHome}
          style={{ cursor: 'pointer' }}
          onError={(e) => {
            console.error('Logo failed to load:', logo);
            e.target.style.display = 'none';
          }}
        />
        
        {/* ✅ Search Bar */}
        <div className={styles.headerSearch}>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={handleSearchFocus}
          />
          <button className={styles.searchBtn} onClick={handleSearchFocus}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </button>
        </div>

        {/* ✅ Right button group — profile + appview + hamburger, always far right on mobile */}
        <div className={styles.mobileRightGroup}>

          {/* Profile avatar — only when logged in */}
          {isAuthenticated && (
            <div className={styles.mobileProfileInline}>
              <div
                className={styles.profileIconWrap}
                onClick={() => setMobileProfileOpen(prev => !prev)}
              >
                {profileImage ? (
                  <img src={profileImage} alt="Profile" className={styles.profileIcon} />
                ) : (
                  <div className={styles.profileIcon}>
                    {user?.identifier?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
                {inboxBadge > 0 && (
                  <span className={styles.profileBadge}>
                    {inboxBadge > 99 ? '99+' : inboxBadge}
                  </span>
                )}
              </div>

              {mobileProfileOpen && (
                <div className={styles.mobileProfileDropdown}>
                  <div className={`${styles.dropdownItem} ${styles.userInfo}`}>
                    {user?.identifier || 'User'}
                  </div>
                  <div
                    className={`${styles.dropdownItem} ${styles.clickable}`}
                    onClick={navigateToProfile}
                  >
                    My Profile
                    {inboxBadge > 0 && (
                      <span className={styles.dropdownBadge}>
                        {inboxBadge > 99 ? '99+' : inboxBadge}
                      </span>
                    )}
                  </div>
                  <div
                    className={`${styles.dropdownItem} ${styles.clickable}`}
                    onClick={() => { openPostAdModal(); setMobileProfileOpen(false); }}
                  >
                    Post an Ad
                  </div>
                  <div
                    className={`${styles.dropdownItem} ${styles.clickable}`}
                    onClick={handleLogout}
                  >
                    Logout
                  </div>
                </div>
              )}
            </div>
          )}

          {/* App View Toggle Button */}
          <button
            className={`${styles.appViewBtn}${appViewActive ? ` ${styles.appViewBtnActive}` : ''}`}
            onClick={() => {
              const next = !appViewActive;
              setAppViewActive(next);
              setCurrentPage(next ? ROUTES.APP_VIEW : ROUTES.HOME);
            }}
            aria-label="Toggle App View"
          >
            <span className={styles.appViewInner} />
          </button>

          {/* Mobile Toggle — SVG icon (☰ / ✕) */}
          <button 
            className={styles.mobileToggle}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
          {mobileMenuOpen ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M3 12h18M3 6h18M3 18h18"/>
            </svg>
          )}
          </button>
        </div>{/* end mobileRightGroup */}

        {/* ✅ Navigation Menu */}
        <nav className={`${styles.menu} ${mobileMenuOpen ? styles.open : ''}`}>
          <a 
            href="#" 
            className={currentPage === ROUTES.HOME ? styles.active : ''} 
            onClick={(e) => { 
              e.preventDefault(); 
              navigateToHome(); 
              setMobileMenuOpen(false); 
            }}
          >
            Home
          </a>

          <a 
            href="#" 
            onClick={(e) => { 
              e.preventDefault(); 
              handleScrollToContact(); 
              setMobileMenuOpen(false); 
            }}
          >
            Contact
          </a>

          {isAuthenticated && isAdmin && (
            <a 
              href="#" 
              className={currentPage === ROUTES.ADMIN ? styles.active : ''} 
              onClick={(e) => { 
                e.preventDefault(); 
                setCurrentPage(ROUTES.ADMIN); 
                setMobileMenuOpen(false); 
              }}
            >
              Admin Dashboard
            </a>
          )}
          
          {/* Mobile-only buttons */}
          <div className={styles.mobileOnly}>
            {!isAuthenticated ? (
              <>
                <button 
                  className="btn btn-outline" 
                  onClick={() => { openLoginModal(); setMobileMenuOpen(false); }}
                >
                  Login
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => { openPostAdModal(); setMobileMenuOpen(false); }}
                >
                  Post an Ad
                </button>
              </>
            ) : (
              /* When logged in: profile is shown inline beside toggle — nothing extra in dropdown */
              null
            )}
          </div>
        </nav>

        {/* ✅ Desktop Actions */}
        <div className={`${styles.actions} ${styles.desktopOnly}`}>
          {!isAuthenticated ? (
            <>
              <button className="btn btn-outline" onClick={openLoginModal}>
                Login
              </button>
              <button className="btn btn-primary" onClick={openPostAdModal}>
                Post an Ad
              </button>
            </>
          ) : (
            <>
              {/* Profile dropdown */}
              <div className={styles.userMenu}>
                <div 
                  className={styles.profileIconWrap} 
                  onClick={() => setDropdownOpen(prev => !prev)}
                >
                  {profileImage ? (
                    <img 
                      src={profileImage} 
                      alt="Profile" 
                      className={styles.profileIcon} 
                    />
                  ) : (
                    <div className={styles.profileIcon}>
                      {user?.identifier?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                  {inboxBadge > 0 && (
                    <span className={styles.profileBadge}>
                      {inboxBadge > 99 ? '99+' : inboxBadge}
                    </span>
                  )}
                </div>
                
                {dropdownOpen && (
                <div className={styles.dropdownMenu}>
                  <div className={`${styles.dropdownItem} ${styles.userInfo}`}>
                    {user?.identifier || 'User'}
                  </div>
                  <div 
                    className={`${styles.dropdownItem} ${styles.clickable}`}
                    onClick={navigateToProfile}
                  >
                    My Profile
                    {inboxBadge > 0 && (
                      <span className={styles.dropdownBadge}>
                        {inboxBadge > 99 ? '99+' : inboxBadge}
                      </span>
                    )}
                  </div>
                  <div 
                    className={`${styles.dropdownItem} ${styles.clickable}`} 
                    onClick={handleLogout}
                  >
                    Logout
                  </div>
                </div>
                )}
              </div>
              
              <button className="btn btn-primary" onClick={openPostAdModal}>
                Post an Ad
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;



































// import React, { useState, useEffect, useCallback } from 'react';
// import { useAuthContext } from '../../../context/AuthContext';
// import { ROUTES } from '../../../utils/constants';
// import styles from './Header.module.css';
// import { shouldFetchBadge } from '../../../utils/authStorage';

// const API_BASE = import.meta.env?.VITE_API_BASE_URL || 'http://192.168.56.1:8000/api';

// function getToken() {
//   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// }

// const Header = ({ 
//   logo, 
//   searchTerm, 
//   setSearchTerm, 
//   handleSearchFocus,
//   handleScrollToTop,
//   currentPage, 
//   setCurrentPage, 
//   openLoginModal, 
//   openPostAdModal 
// }) => {
//   const { user, isAuthenticated, isAdmin, profileImage, logout } = useAuthContext();
//   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
//   const [dropdownOpen, setDropdownOpen] = useState(false);
//   const [inboxBadge, setInboxBadge] = useState(0);

//   // ✅ Fetch notification badge count
//   const fetchBadgeCount = useCallback(async () => {
//     if (!isAuthenticated) { 
//       setInboxBadge(0); 
//       return; 
//     }

//     const token = getToken();
//     if (!token) return;

//     // ✅ SUSPENSION CHECK — always runs regardless of debounce.
//     // Checks is_active on every mount, focus, and tab-switch.
//     // If admin has suspended this account, log out immediately.
//     try {
//       const profileRes = await fetch(`${API_BASE}/auth/profile/`, {
//         headers: { Authorization: `Token ${token}` },
//       });

//       // ✅ KEY FIX: Django TokenAuthentication returns 401 when is_active=False.
//       // The view never runs, so the body never contains is_active=false.
//       // Treat 401/403 as a suspended/invalid session → force logout immediately.
//       if (profileRes.status === 401 || profileRes.status === 403) {
//         console.warn('🔒 Session rejected (401/403) — forcing logout.');
//         logout();
//         setCurrentPage(ROUTES.HOME);
//         return;
//       }

//       if (profileRes.ok) {
//         const profileData = await profileRes.json();
//         if (profileData.is_active === false) {
//           console.warn('Account suspended — logging out.');
//           logout();
//           setCurrentPage(ROUTES.HOME);
//           return; // stop — don't fetch badges for a suspended user
//         }
//       }
//     } catch (err) {
//       console.error('Suspension check error:', err);
//     }

//     // ✅ Shared debounce — if ProfilePage already fetched within 5s, skip badge fetch.
//     // Prevents double-burst when Header focus event fires simultaneously
//     // with ProfilePage's own fetch (e.g. Francis on profile page switching tabs).
//     if (!shouldFetchBadge()) return;
    
//     try {
//       const [convoRes, likesRes, followersRes, sysMsgRes] = await Promise.all([
//         fetch(`${API_BASE}/messages/conversations/`, { headers: { Authorization: `Token ${token}` } }),
//         fetch(`${API_BASE}/my/likes/`,               { headers: { Authorization: `Token ${token}` } }),
//         fetch(`${API_BASE}/my/followers/`,           { headers: { Authorization: `Token ${token}` } }),
//         fetch(`${API_BASE}/auth/system-messages/`,   { headers: { Authorization: `Token ${token}` } }),
//       ]);

//       const convoData     = convoRes.ok    ? await convoRes.json()    : { conversations: [] };
//       const likesData     = likesRes.ok    ? await likesRes.json()    : { likes: [] };
//       const followersData = followersRes.ok ? await followersRes.json() : { followers: [] };
//       const sysMsgData    = sysMsgRes.ok   ? await sysMsgRes.json()   : { unread_count: 0 };

//       const userEmail = (user?.identifier || user?.email || '').toLowerCase();
//       const conversations = convoData.conversations || [];

//       // Count unread messages
//       const unreadDMs = conversations.filter(c => {
//         const isBuyer = (c.buyer?.email || '').toLowerCase() === userEmail;
//         return isBuyer ? (c.unread_by_buyer > 0) : (c.unread_by_seller > 0);
//       }).length;

//       // Count new likes/followers
//       const likes = (likesData.likes || []).length;
//       const followers = (followersData.followers || []).length;
//       const seenLikes = parseInt(localStorage.getItem('seenLikesCount') || '0', 10);
//       const seenFollowers = parseInt(localStorage.getItem('seenFollowersCount') || '0', 10);
//       const newLikes = Math.max(0, likes - seenLikes);
//       const newFollowers = Math.max(0, followers - seenFollowers);

//       // ✅ Include I'm Selling unread system messages in badge count
//       const imSellingUnread = sysMsgData.unread_count || 0;

//       setInboxBadge(unreadDMs + newLikes + newFollowers + imSellingUnread);
//     } catch (err) {
//       console.error('Badge fetch error:', err);
//     }
//   }, [isAuthenticated, user, logout, setCurrentPage]);

//   // ✅ Fetch on mount, on window focus, and on tab becoming visible.
//   //    Replaces the old 60s setInterval — no more background polling noise.
//   //    Requests only fire when the user is actually looking at the page.
//   useEffect(() => {
//     // 1. Fetch immediately on mount
//     fetchBadgeCount();

//     // 2. Fetch when user returns to this browser window from another app
//     window.addEventListener('focus', fetchBadgeCount);

//     // 3. Fetch when user switches back to this tab from another tab
//     //    document.visibilityState === 'visible' means tab is now in front
//     const handleVisibility = () => {
//       if (document.visibilityState === 'visible') fetchBadgeCount();
//     };
//     document.addEventListener('visibilitychange', handleVisibility);

//     // Cleanup both listeners on unmount
//     return () => {
//       window.removeEventListener('focus', fetchBadgeCount);
//       document.removeEventListener('visibilitychange', handleVisibility);
//     };
//   }, [fetchBadgeCount]);

//   // ✅ Navigation handlers
//   const handleLogout = () => {
//     logout();
//     setMobileMenuOpen(false);
//     setCurrentPage(ROUTES.HOME);
//   };

//   const navigateToHome = () => {
//     setCurrentPage(ROUTES.HOME);
//     if (handleScrollToTop) handleScrollToTop();
//   };

//   const navigateToProfile = () => {
//     setCurrentPage(ROUTES.PROFILE);
//     setMobileMenuOpen(false);
//   };

//   const handleScrollToContact = () => {
//     const footer = document.querySelector('footer');
//     if (footer) {
//       footer.scrollIntoView({ behavior: 'smooth', block: 'start' });
//     }
//   };

//   return (
//     <header className={styles.mainHeader}>
//       <div className={styles.navContainer}>
//         {/* ✅ Logo */}
//         <img 
//           src={logo} 
//           alt="I'm Selling Logo" 
//           className={styles.siteLogo} 
//           onClick={navigateToHome}
//           style={{ cursor: 'pointer' }}
//           onError={(e) => {
//             console.error('Logo failed to load:', logo);
//             e.target.style.display = 'none';
//           }}
//         />
        
//         {/* ✅ Search Bar */}
//         <div className={styles.headerSearch}>
//           <input
//             type="text"
//             placeholder="Search products..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             onFocus={handleSearchFocus}
//           />
//           <button className={styles.searchBtn} onClick={handleSearchFocus}>
//             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//               <circle cx="11" cy="11" r="8" />
//               <path d="m21 21-4.35-4.35" />
//             </svg>
//           </button>
//         </div>

//         {/* ✅ Mobile Toggle */}
//         <button 
//           className={styles.mobileToggle} 
//           onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
//           aria-label="Toggle menu"
//         >
//           ☰
//         </button>

//         {/* ✅ Navigation Menu */}
//         <nav className={`${styles.menu} ${mobileMenuOpen ? styles.open : ''}`}>
//           <a 
//             href="#" 
//             className={currentPage === ROUTES.HOME ? styles.active : ''} 
//             onClick={(e) => { 
//               e.preventDefault(); 
//               navigateToHome(); 
//               setMobileMenuOpen(false); 
//             }}
//           >
//             Home
//           </a>

//           <a 
//             href="#" 
//             onClick={(e) => { 
//               e.preventDefault(); 
//               handleScrollToContact(); 
//               setMobileMenuOpen(false); 
//             }}
//           >
//             Contact
//           </a>

//           {isAuthenticated && isAdmin && (
//             <a 
//               href="#" 
//               className={currentPage === ROUTES.ADMIN ? styles.active : ''} 
//               onClick={(e) => { 
//                 e.preventDefault(); 
//                 setCurrentPage(ROUTES.ADMIN); 
//                 setMobileMenuOpen(false); 
//               }}
//             >
//               Admin Dashboard
//             </a>
//           )}
          
//           {/* Mobile-only buttons */}
//           <div className={styles.mobileOnly}>
//             {!isAuthenticated ? (
//               <>
//                 <button 
//                   className="btn btn-outline" 
//                   onClick={() => { openLoginModal(); setMobileMenuOpen(false); }}
//                 >
//                   Login
//                 </button>
//                 <button 
//                   className="btn btn-primary" 
//                   onClick={() => { openPostAdModal(); setMobileMenuOpen(false); }}
//                 >
//                   Post an Ad
//                 </button>
//               </>
//             ) : (
//               <div className={styles.mobileUserMenu}>
//                 <div className={styles.userInfoMobile}>
//                   {profileImage ? (
//                     <img 
//                       src={profileImage} 
//                       alt="Profile" 
//                       className={styles.profileIconMobile} 
//                     />
//                   ) : (
//                     <div className={styles.profileIconMobile}>
//                       {user?.identifier?.[0]?.toUpperCase() || 'U'}
//                     </div>
//                   )}
//                   <span>{user?.identifier || 'User'}</span>
//                   {inboxBadge > 0 && (
//                     <span className={styles.mobileBadge}>
//                       {inboxBadge > 99 ? '99+' : inboxBadge}
//                     </span>
//                   )}
//                 </div>
//                 <button className="btn btn-outline" onClick={navigateToProfile}>
//                   My Profile
//                 </button>
//                 <button 
//                   className="btn btn-outline" 
//                   onClick={() => { openPostAdModal(); setMobileMenuOpen(false); }}
//                 >
//                   Post an Ad
//                 </button>
//                 <button className="btn btn-outline" onClick={handleLogout}>
//                   Logout
//                 </button>
//               </div>
//             )}
//           </div>
//         </nav>

//         {/* ✅ Desktop Actions */}
//         <div className={`${styles.actions} ${styles.desktopOnly}`}>
//           {!isAuthenticated ? (
//             <>
//               <button className="btn btn-outline" onClick={openLoginModal}>
//                 Login
//               </button>
//               <button className="btn btn-primary" onClick={openPostAdModal}>
//                 Post an Ad
//               </button>
//             </>
//           ) : (
//             <>
//               {/* Profile dropdown */}
//               <div 
//                 className={styles.userMenu}
//                 onMouseEnter={() => setDropdownOpen(true)}
//                 onMouseLeave={() => setDropdownOpen(false)}
//               >
//                 <div className={styles.profileIconWrap}>
//                   {profileImage ? (
//                     <img 
//                       src={profileImage} 
//                       alt="Profile" 
//                       className={styles.profileIcon} 
//                     />
//                   ) : (
//                     <div className={styles.profileIcon}>
//                       {user?.identifier?.[0]?.toUpperCase() || 'U'}
//                     </div>
//                   )}
//                   {inboxBadge > 0 && (
//                     <span className={styles.profileBadge}>
//                       {inboxBadge > 99 ? '99+' : inboxBadge}
//                     </span>
//                   )}
//                 </div>
                
//                 {/* Only render dropdown when open */}
//                 {dropdownOpen && (
//                   <div className={styles.dropdownMenu}>
//                     <div className={`${styles.dropdownItem} ${styles.userInfo}`}>
//                       {user?.identifier || 'User'}
//                     </div>
//                     <div 
//                       className={`${styles.dropdownItem} ${styles.clickable}`}
//                       onClick={navigateToProfile}
//                     >
//                       My Profile
//                       {inboxBadge > 0 && (
//                         <span className={styles.dropdownBadge}>
//                           {inboxBadge > 99 ? '99+' : inboxBadge}
//                         </span>
//                       )}
//                     </div>
//                     <div 
//                       className={`${styles.dropdownItem} ${styles.clickable}`} 
//                       onClick={handleLogout}
//                     >
//                       Logout
//                     </div>
//                   </div>
//                 )}
//               </div>
              
//               <button className="btn btn-primary" onClick={openPostAdModal}>
//                 Post an Ad
//               </button>
//             </>
//           )}
//         </div>
//       </div>
//     </header>
//   );
// };

// export default Header;

















// // import React, { useState, useEffect, useCallback } from 'react';
// // import { useAuthContext } from '../../../context/AuthContext';
// // import { ROUTES } from '../../../utils/constants';
// // import styles from './Header.module.css';
// // import { shouldFetchBadge } from '../../../utils/authStorage';

// // const API_BASE = process.env.REACT_APP_API_URL || 'https://imselling-marketplace-production.up.railway.app/api';

// // function getToken() {
// //   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// // }

// // const Header = ({ 
// //   logo, 
// //   searchTerm, 
// //   setSearchTerm, 
// //   handleSearchFocus,
// //   handleScrollToTop,
// //   currentPage, 
// //   setCurrentPage, 
// //   openLoginModal, 
// //   openPostAdModal 
// // }) => {
// //   const { user, isAuthenticated, isAdmin, profileImage, logout } = useAuthContext();
// //   const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
// //   const [inboxBadge, setInboxBadge] = useState(0);

// //   // ✅ Fetch notification badge count
// //   const fetchBadgeCount = useCallback(async () => {
// //     if (!isAuthenticated) { 
// //       setInboxBadge(0); 
// //       return; 
// //     }

// //     const token = getToken();
// //     if (!token) return;

// //     // ✅ SUSPENSION CHECK — always runs regardless of debounce.
// //     // Checks is_active on every mount, focus, and tab-switch.
// //     // If admin has suspended this account, log out immediately.
// //     try {
// //       const profileRes = await fetch(`${API_BASE}/auth/profile/`, {
// //         headers: { Authorization: `Token ${token}` },
// //       });

// //       // ✅ KEY FIX: Django TokenAuthentication returns 401 when is_active=False.
// //       // The view never runs, so the body never contains is_active=false.
// //       // Treat 401/403 as a suspended/invalid session → force logout immediately.
// //       if (profileRes.status === 401 || profileRes.status === 403) {
// //         console.warn('🔒 Session rejected (401/403) — forcing logout.');
// //         logout();
// //         setCurrentPage(ROUTES.HOME);
// //         return;
// //       }

// //       if (profileRes.ok) {
// //         const profileData = await profileRes.json();
// //         if (profileData.is_active === false) {
// //           console.warn('Account suspended — logging out.');
// //           logout();
// //           setCurrentPage(ROUTES.HOME);
// //           return; // stop — don't fetch badges for a suspended user
// //         }
// //       }
// //     } catch (err) {
// //       console.error('Suspension check error:', err);
// //     }

// //     // ✅ Shared debounce — if ProfilePage already fetched within 5s, skip badge fetch.
// //     // Prevents double-burst when Header focus event fires simultaneously
// //     // with ProfilePage's own fetch (e.g. Francis on profile page switching tabs).
// //     if (!shouldFetchBadge()) return;
    
// //     try {
// //       const [convoRes, likesRes, followersRes, sysMsgRes] = await Promise.all([
// //         fetch(`${API_BASE}/messages/conversations/`, { headers: { Authorization: `Token ${token}` } }),
// //         fetch(`${API_BASE}/my/likes/`,               { headers: { Authorization: `Token ${token}` } }),
// //         fetch(`${API_BASE}/my/followers/`,           { headers: { Authorization: `Token ${token}` } }),
// //         fetch(`${API_BASE}/auth/system-messages/`,   { headers: { Authorization: `Token ${token}` } }),
// //       ]);

// //       const convoData     = convoRes.ok    ? await convoRes.json()    : { conversations: [] };
// //       const likesData     = likesRes.ok    ? await likesRes.json()    : { likes: [] };
// //       const followersData = followersRes.ok ? await followersRes.json() : { followers: [] };
// //       const sysMsgData    = sysMsgRes.ok   ? await sysMsgRes.json()   : { unread_count: 0 };

// //       const userEmail = (user?.identifier || user?.email || '').toLowerCase();
// //       const conversations = convoData.conversations || [];

// //       // Count unread messages
// //       const unreadDMs = conversations.filter(c => {
// //         const isBuyer = (c.buyer?.email || '').toLowerCase() === userEmail;
// //         return isBuyer ? (c.unread_by_buyer > 0) : (c.unread_by_seller > 0);
// //       }).length;

// //       // Count new likes/followers
// //       const likes = (likesData.likes || []).length;
// //       const followers = (followersData.followers || []).length;
// //       const seenLikes = parseInt(localStorage.getItem('seenLikesCount') || '0', 10);
// //       const seenFollowers = parseInt(localStorage.getItem('seenFollowersCount') || '0', 10);
// //       const newLikes = Math.max(0, likes - seenLikes);
// //       const newFollowers = Math.max(0, followers - seenFollowers);

// //       // ✅ Include I'm Selling unread system messages in badge count
// //       const imSellingUnread = sysMsgData.unread_count || 0;

// //       setInboxBadge(unreadDMs + newLikes + newFollowers + imSellingUnread);
// //     } catch (err) {
// //       console.error('Badge fetch error:', err);
// //     }
// //   }, [isAuthenticated, user, logout, setCurrentPage]);

// //   // ✅ Fetch on mount, on window focus, and on tab becoming visible.
// //   //    Replaces the old 60s setInterval — no more background polling noise.
// //   //    Requests only fire when the user is actually looking at the page.
// //   useEffect(() => {
// //     // 1. Fetch immediately on mount
// //     fetchBadgeCount();

// //     // 2. Fetch when user returns to this browser window from another app
// //     window.addEventListener('focus', fetchBadgeCount);

// //     // 3. Fetch when user switches back to this tab from another tab
// //     //    document.visibilityState === 'visible' means tab is now in front
// //     const handleVisibility = () => {
// //       if (document.visibilityState === 'visible') fetchBadgeCount();
// //     };
// //     document.addEventListener('visibilitychange', handleVisibility);

// //     // Cleanup both listeners on unmount
// //     return () => {
// //       window.removeEventListener('focus', fetchBadgeCount);
// //       document.removeEventListener('visibilitychange', handleVisibility);
// //     };
// //   }, [fetchBadgeCount]);

// //   // ✅ Navigation handlers
// //   const handleLogout = () => {
// //     logout();
// //     setMobileMenuOpen(false);
// //     setCurrentPage(ROUTES.HOME);
// //   };

// //   const navigateToHome = () => {
// //     setCurrentPage(ROUTES.HOME);
// //     if (handleScrollToTop) handleScrollToTop();
// //   };

// //   const navigateToProfile = () => {
// //     setCurrentPage(ROUTES.PROFILE);
// //     setMobileMenuOpen(false);
// //   };

// //   const handleScrollToContact = () => {
// //     const footer = document.querySelector('footer');
// //     if (footer) {
// //       footer.scrollIntoView({ behavior: 'smooth', block: 'start' });
// //     }
// //   };

// //   return (
// //     <header className={styles.mainHeader}>
// //       <div className={styles.navContainer}>
// //         {/* ✅ Logo */}
// //         <img 
// //           src={logo} 
// //           alt="I'm Selling Logo" 
// //           className={styles.siteLogo} 
// //           onClick={navigateToHome}
// //           style={{ cursor: 'pointer' }}
// //           onError={(e) => {
// //             console.error('Logo failed to load:', logo);
// //             e.target.style.display = 'none';
// //           }}
// //         />
        
// //         {/* ✅ Search Bar */}
// //         <div className={styles.headerSearch}>
// //           <input
// //             type="text"
// //             placeholder="Search products..."
// //             value={searchTerm}
// //             onChange={(e) => setSearchTerm(e.target.value)}
// //             onFocus={handleSearchFocus}
// //           />
// //           <button className={styles.searchBtn} onClick={handleSearchFocus}>
// //             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
// //               <circle cx="11" cy="11" r="8" />
// //               <path d="m21 21-4.35-4.35" />
// //             </svg>
// //           </button>
// //         </div>

// //         {/* ✅ Mobile Toggle */}
// //         <button 
// //           className={styles.mobileToggle}
// //           onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
// //           aria-label="Toggle menu"
// //         >
// //           {mobileMenuOpen ? (
// //             <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
// //               <path d="M18 6L6 18M6 6l12 12"/>
// //             </svg>
// //           ) : (
// //             <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
// //               <path d="M3 12h18M3 6h18M3 18h18"/>
// //             </svg>
// //           )}
// //         </button>

// //         {/* ✅ Navigation Menu */}
// //         <nav className={`${styles.menu} ${mobileMenuOpen ? styles.open : ''}`}>
// //           <a 
// //             href="#" 
// //             className={currentPage === ROUTES.HOME ? styles.active : ''} 
// //             onClick={(e) => { 
// //               e.preventDefault(); 
// //               navigateToHome(); 
// //               setMobileMenuOpen(false); 
// //             }}
// //           >
// //             Home
// //           </a>

// //           <a 
// //             href="#" 
// //             onClick={(e) => { 
// //               e.preventDefault(); 
// //               handleScrollToContact(); 
// //               setMobileMenuOpen(false); 
// //             }}
// //           >
// //             Contact
// //           </a>

// //           {isAuthenticated && isAdmin && (
// //             <a 
// //               href="#" 
// //               className={currentPage === ROUTES.ADMIN ? styles.active : ''} 
// //               onClick={(e) => { 
// //                 e.preventDefault(); 
// //                 setCurrentPage(ROUTES.ADMIN); 
// //                 setMobileMenuOpen(false); 
// //               }}
// //             >
// //               Admin Dashboard
// //             </a>
// //           )}
          
// //           {/* Mobile-only buttons */}
// //           <div className={styles.mobileOnly}>
// //             {!isAuthenticated ? (
// //               <>
// //                 <button 
// //                   className="btn btn-outline" 
// //                   onClick={() => { openLoginModal(); setMobileMenuOpen(false); }}
// //                 >
// //                   Login
// //                 </button>
// //                 <button 
// //                   className="btn btn-primary" 
// //                   onClick={() => { openPostAdModal(); setMobileMenuOpen(false); }}
// //                 >
// //                   Post an Ad
// //                 </button>
// //               </>
// //             ) : (
// //               <div className={styles.mobileUserMenu}>
// //                 <div className={styles.userInfoMobile}>
// //                   {profileImage ? (
// //                     <img 
// //                       src={profileImage} 
// //                       alt="Profile" 
// //                       className={styles.profileIconMobile} 
// //                     />
// //                   ) : (
// //                     <div className={styles.profileIconMobile}>
// //                       {user?.identifier?.[0]?.toUpperCase() || 'U'}
// //                     </div>
// //                   )}
// //                   <span>{user?.identifier || 'User'}</span>
// //                   {inboxBadge > 0 && (
// //                     <span className={styles.mobileBadge}>
// //                       {inboxBadge > 99 ? '99+' : inboxBadge}
// //                     </span>
// //                   )}
// //                 </div>
// //                 <button className="btn btn-outline" onClick={navigateToProfile}>
// //                   My Profile
// //                 </button>
// //                 <button 
// //                   className="btn btn-outline" 
// //                   onClick={() => { openPostAdModal(); setMobileMenuOpen(false); }}
// //                 >
// //                   Post an Ad
// //                 </button>
// //                 <button className="btn btn-outline" onClick={handleLogout}>
// //                   Logout
// //                 </button>
// //               </div>
// //             )}
// //           </div>
// //         </nav>

// //         {/* ✅ Desktop Actions */}
// //         <div className={`${styles.actions} ${styles.desktopOnly}`}>
// //           {!isAuthenticated ? (
// //             <>
// //               <button className="btn btn-outline" onClick={openLoginModal}>
// //                 Login
// //               </button>
// //               <button className="btn btn-primary" onClick={openPostAdModal}>
// //                 Post an Ad
// //               </button>
// //             </>
// //           ) : (
// //             <>
// //               {/* Profile dropdown */}
// //               <div className={styles.userMenu}>
// //                 <div className={styles.profileIconWrap}>
// //                   {profileImage ? (
// //                     <img 
// //                       src={profileImage} 
// //                       alt="Profile" 
// //                       className={styles.profileIcon} 
// //                     />
// //                   ) : (
// //                     <div className={styles.profileIcon}>
// //                       {user?.identifier?.[0]?.toUpperCase() || 'U'}
// //                     </div>
// //                   )}
// //                   {inboxBadge > 0 && (
// //                     <span className={styles.profileBadge}>
// //                       {inboxBadge > 99 ? '99+' : inboxBadge}
// //                     </span>
// //                   )}
// //                 </div>
                
// //                 <div className={styles.dropdownMenu}>
// //                   <div className={`${styles.dropdownItem} ${styles.userInfo}`}>
// //                     {user?.identifier || 'User'}
// //                   </div>
// //                   <div 
// //                     className={`${styles.dropdownItem} ${styles.clickable}`}
// //                     onClick={navigateToProfile}
// //                   >
// //                     My Profile
// //                     {inboxBadge > 0 && (
// //                       <span className={styles.dropdownBadge}>
// //                         {inboxBadge > 99 ? '99+' : inboxBadge}
// //                       </span>
// //                     )}
// //                   </div>
// //                   <div 
// //                     className={`${styles.dropdownItem} ${styles.clickable}`} 
// //                     onClick={handleLogout}
// //                   >
// //                     Logout
// //                   </div>
// //                 </div>
// //               </div>
              
// //               <button className="btn btn-primary" onClick={openPostAdModal}>
// //                 Post an Ad
// //               </button>
// //             </>
// //           )}
// //         </div>
// //       </div>
// //     </header>
// //   );
// // };

// // export default Header;



